<template>
  <div>
    <!-- Alert: No item found -->
    <b-alert variant="danger" :show="userData === undefined">
      <h4 class="alert-heading">
        Error fetching user data
      </h4>
      <div class="alert-body">
        No user found with this user id. Check
        <b-link class="alert-link" :to="{ name: 'apps-users-list' }">
          User List
        </b-link>
        for other users.
      </div>
    </b-alert>

    <template v-if="userData">
      <b-row>
        <b-col cols="12" xl="12" lg="12" md="12">
          <b-row>
            <b-col lg="4" xl="4" md="4">
              <b-button
                variant="outline-secondary"
                block
                @click="
                  prevRoute.path !== '/'
                    ? $router.back()
                    : $router.push({ name: 'apps-applications-list' })
                "
              >
                {{ $t('application.btn.back') }}
              </b-button>
              <interview-updates
                :application-notifications="
                  userData.applicationNotifications && userData.applicationNotifications.length > 0
                    ? userData.applicationNotifications.filter(
                      item => item === 'document_request_answer'
                    ).length
                    : 0
                "
                :application-updates="applicationUpdates"
              />
              <interview-card
                v-if="!userData.confirmed"
                :interview="interview"
                :user-data="userData"
                @action="onAction"
              />
              <contract-card
                v-if="userData.confirmed && !hideContractCard"
                :user-data="userData"
                :processer="processer"
              />
            </b-col>
            <b-col lg="8" style="height: 100vh; overflow: auto">
              <!-- First Row -->
              <b-row>
                <b-col cols="12" xl="12" lg="12" md="12">
                  <personel-view-info-card :user-data="userData" @action="onAction" />
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" lg="6">
                  <personel-text-card v-if="userData.about" :title="$t('about')" :text="userData.about" />
                </b-col>
                <b-col cols="12" lg="6">
                  <personel-text-card v-if="userData.workHistory" :title="$t('work_history')" :text="userData.workHistory" />
                </b-col>
              </b-row>
              <b-row v-if="userData && userData.files && userData.files.length > 0">
                <b-col cols="12" lg="12">
                  <personel-files-card :user-data="userData" />
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" lg="12">
                  <personel-anwers-card :interview="interview" />
                </b-col>
              </b-row>
              <b-row v-if="requestedDocuments && requestedDocuments.length > 0">
                <b-col cols="12" xl="12" lg="12" md="12">
                  <document-list :documents="requestedDocuments" @action="onAction" />
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" xl="12" lg="12" md="12">
                  <comment-info :user-data="userData" />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import store from '@/shared/store'
// eslint-disable-next-line import/no-cycle
import router from '@/shared/router'
import { ref } from '@vue/composition-api'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import { BRow, BCol, BAlert, BLink, BButton } from 'bootstrap-vue'
import PersonelViewInfoCard from './PersonelViewInfoCard.vue'
import PersonelFilesCard from './PersonelFilesCard.vue'
import PersonelTextCard from './PersonelTextCard.vue'
import InterviewCard from './InterviewCard.vue'
import ContractCard from './ContractCard.vue'
import InterviewUpdates from './InterviewUpdates.vue'
import PersonelAnwersCard from './PersonelAnwersCard.vue'
import DocumentList from './DocumentList.vue'
import CommentInfo from './CommentInfo.vue'

export default {
  components: {
    BRow,
    BCol,
    BAlert,
    BLink,
    BButton,

    // Local Components
    PersonelViewInfoCard,
    PersonelFilesCard,
    PersonelTextCard,
    InterviewCard,
    ContractCard,
    InterviewUpdates,
    PersonelAnwersCard,
    DocumentList,
    CommentInfo,
  },
  data() {
    return {
      hideContractCard: false,
      processer: {},
      updates: [],
      prevRoute: null,
    }
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser').then(res => {
      const user = JSON.parse(JSON.stringify(res))
      this.processer = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      }
    })
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // eslint-disable-next-line no-param-reassign
      vm.prevRoute = from
    })
  },
  methods: {
    onAction(action, data) {
      const update = {
        state: action,
        date: new Date(),
        processer: this.processer,
        duration: data.duration,
        content: data.content ? data.content : {},
      }
      let updateState = null
      if (data.update) {
        updateState = {}
        updateState = {
          ...data.update,
        }
      }
      if (data.loveGranted) {
        update.loveGranted = data.loveGranted
      }
      if (data.graduationDate) {
        update.graduationDate = data.graduationDate
      }
      if (data.creditPoints) {
        update.studies = {
          ...this.userData.studies,
          credits: data.creditPoints,
        }
      }
      this.updateApplication(update, updateState)
    },
    async updateApplication(update, updateState) {
      this.hideContractCard = true
      const toastId = this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: this.$t('application.notification.willUpdate'),
            icon: 'UserIcon',
            variant: 'info',
          },
        },
        {
          timeout: 0,
        }
      )
      const previousUpdates = this.userData.applicationUpdates
        ? this.userData.applicationUpdates
        : []
      const formattedUpdate = update
      let newState = {
        ...updateState,
        updateDate: update.date,
        status: updateState && updateState.status ? updateState.status : 'pending',
      }
      if (update.state === 'rejected') {
        newState = {
          ...newState,
          confirmed: false,
          role:
            this.userData.profession === 'sairaanhoitaja'
              ? this.userData.profession
              : 'lähihoitaja',
          status: 'rejected',
        }
      }

      if (update.state === 'request_documents') {
        const previousRequestedDocuments = this.userData.requestedDocuments
          ? this.userData.requestedDocuments
          : []
        const zendeskData = await this.onCreateZendeskTicketRequestDocs(update.content.message)
        const documents = update.content.message.map(doc => ({
          name: doc,
          confirmed: false,
          ticketId: zendeskData.id,
        }))
        newState.requestedDocuments = previousRequestedDocuments.concat(documents)
        formattedUpdate.content.message = `${zendeskData.id}: ${update.content.message.join(',')}`
        await store.dispatch('appApplications/createClientRequest', {
          assignedTo: this.userData.id,
          ticketId: zendeskData.id.toString(),
          processer: `${this.processer.firstName} ${this.processer.lastName}`,
          type: 'application_request',
          status: 'open',
          updates: 0,
        })
      }

      if (update.state === 'remove_documents') {
        const previousRequestedDocuments = this.userData.requestedDocuments
        const newDocuments = previousRequestedDocuments.filter(
          item => item.name !== update.content.message
        )
        newState.requestedDocuments = newDocuments
        formattedUpdate.content.message = update.content.message
      }

      if (update.state === 'confirmed_documents') {
        newState.requestedDocuments = this.userData.requestedDocuments.map(document => {
          if (update.content.message.indexOf(document.name) > -1) {
            return {
              name: document.name,
              ticketId: document.ticketId ? document.ticketId : '',
              confirmed: true,
            }
          }
          return document
        })
        formattedUpdate.content.message = update.content.message.join(', ')
        if (
          !newState.requestedDocuments.some(doc => !doc.confirmed) &&
          this.userData.applicationNotifications &&
          this.userData.applicationNotifications.length > 0
        ) {
          newState.applicationNotifications = this.userData.applicationNotifications.filter(
            item => item !== 'document_request_answer'
          )
          await store.dispatch('appApplications/closeClientRequest', this.userData.id)
        }
        if (update.loveGranted) {
          newState.loveGranted = update.loveGranted
        }
        if (update.graduationDate) {
          newState.graduationDate = update.graduationDate
        }
        if (update.studies) {
          newState.studies = update.studies
        }
      }

      if (update.state === 'send_sms') {
        await this.sendSMS(update.content.message)
        formattedUpdate.content.message = `Send SMS to: ${this.userData.mobile} `
      }

      if (update.state === 'send_message') {
        const { to, subject, message } = update.content
        const zendeskData = await this.onCreateZendeskTicketCustom(to, subject, message)
        formattedUpdate.content.message = zendeskData.id
      }
      if (update.state === 'confirm_valvira') {
        newState.valviraChecked = true
        newState.hasValvira = true
      }
      if (update.state === 'no_valvira') {
        newState.valviraChecked = true
        newState.hasValvira = false
      }

      if (update.state === 'confirm_interview') {
        newState.isInterviewed = true
      }

      if (update.state === 'change_status_love') {
        const { love, to, subject, message } = update.content
        newState.love = love
        await this.sendEmail(to, message, subject)
        // Remove from require documents
        const previousRequestedDocuments = this.userData.requestedDocuments
        const newDocuments = previousRequestedDocuments.filter(
          item => item.name !== 'Lääkehoitolupa (teoria ja näytöt)'
        )
        newState.requestedDocuments = newDocuments
        formattedUpdate.content.message = 'Muutettu luvattomaksi'
      }

      newState.applicationUpdates = previousUpdates.concat(formattedUpdate)

      store
        .dispatch('appApplications/updateUser', { id: router.currentRoute.params.id, newState })
        .then(() => {
          this.$toast.dismiss(toastId)
          this.hideContractCard = false
          if (update.state === 'accepted' || update.state === 'rejected') {
            this.$swal({
              icon: 'success',
              title: this.$t('application.notification.successUpdate'),
              showConfirmButton: true,
              timer: 0,
              confirmButtonText: this.$t('application.notification.backToList'),
              customClass: {
                confirmButton: 'btn btn-primary',
              },
              buttonsStyling: false,
            }).then(result => {
              if (result.isConfirmed) {
                this.$router.back()
              }
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('application.notification.applicationUpdated'),
                icon: 'UserCheckIcon',
                variant: 'success',
              },
            })
          }
        })
        .catch(() => {
          this.hideContractCard = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('error'),
              text: this.$t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    async createConfirmationTicket({ requester = null, groupId, message, subject, collaborators }) {
      const response = await store.dispatch('appApplications/createZendeskTicket', {
        requester,
        groupId,
        message,
        subject,
        collaborators,
      })
      return response.data
    },
    async onCreateZendeskTicketRequestDocs(documentList) {
      const to = this.userData.email
      let message = ''
      const subject = 'Asiakirjojen pyyntö'
      const messageStart = `<p style="text-transform: capitalize">Hei ${this.userData.firstName},</p>
                      <p>Kiitos hakemuksestasi, hakemuksesi viimeistelemiseksi tarvitsemme sinulta seuraavat asiakirjat:</p>`
      const messageList = `<ul>${documentList
        .map(doc => {
          if (doc === 'Hoiwa - lomake') {
            return '<li><b><a href="https://firebasestorage.googleapis.com/v0/b/hoiwa-app.appspot.com/o/lomake%2FHoiwa%20-%20lomake.pdf?alt=media&token=4e425b69-b630-4cee-92dd-f476a5195405" target="_blank">Hoiwa -lomake</a></b> täytettynä</li>'
          }
          return `<li><b>${doc}</b></li>`
        })
        .join('')}</ul>`
      const messageEnd = `
      <p>Voit lähettää listatut asiakirjat liitteenä vastaamalla tähän viestiin.</p>
                          <p>Ystävällisin terveisin,</p><p>Hoiwa - tiimi</p>`
      message = messageStart + messageList + messageEnd
      const result = await this.createConfirmationTicket({
        requester: this.processer.email,
        groupId: '1900000096533',
        message,
        collaborators: to,
        subject,
      })
      return result
    },

    async sendEmail(to, message, subject) {
      try {
        await store.dispatch('appApplications/sendEmail', { to, message, subject })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Viesti lähetetty',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('error'),
            text: this.$t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    sendSMS(body) {
      const { mobile } = this.userData
      store
        .dispatch('appApplications/sendSMS', {
          mobile: this.formatMobile(mobile),
          message: body,
        })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'SMS sent',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          return mobile
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('error'),
              text: this.$t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    changeStatusLove({ status }) {
      const { id } = this.userData
      store
        .dispatch('appApplications/changeStatusLove', {
          id,
          status,
        })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Change status love success',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('error'),
              text: this.$t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    formatMobile(mobile) {
      if (mobile.charAt(0) === '0') {
        return `+358${mobile.substring(1)}`
      }
      if (mobile.charAt(0) !== '+') {
        return `+358${mobile}`
      }

      return mobile
    },
    async onCreateZendeskTicketCustom(to, subject, message) {
      const result = await this.createConfirmationTicket({
        requester: this.processer.email,
        groupId: '360007947637',
        message,
        collaborators: to,
        subject,
      })
      return result
    },
  },
  setup() {
    const userData = ref(null)
    const requestedDocuments = ref([])
    const interview = ref({})
    const applicationUpdates = ref([])

    store.dispatch('appApplications/fetchApplication', {
      id: router.currentRoute.params.id,
      callback: response => {
        userData.value = response
        requestedDocuments.value = userData.value.requestedDocuments
        let updates = [
          {
            state: 'applied',
            date: response.created,
            content: {},
            processer: {
              firstName: response.firstName,
              lastName: response.lastName,
            },
          },
        ]
        if (response.applicationUpdates && response.applicationUpdates.length > 0) {
          updates = response.applicationUpdates
            .concat(updates)
            .sort((a, b) => b.date.toMillis() - a.date.toMillis())
        }
        applicationUpdates.value = updates
      },
    })

    store.dispatch('appApplications/fetchInterview', {
      assignedTo: router.currentRoute.params.id,
      callback: response => {
        if (response) {
          interview.value = response
        }
      },
    })

    return {
      userData,
      interview,
      applicationUpdates,
      requestedDocuments,
    }
  },
}
</script>

<style></style>
